import clsx from 'clsx';
import styles from './studyUsers.module.scss';
import TablePageLayout from 'src/components/tablePageLayout/TablePageLayout';
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { FC, useCallback, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { pagingSortingToBackendRequest } from 'src/helpers/pagination-helper';
import { IStudyStaffDto, ITagFiltersDto, StudyRoleClaimValue } from 'src/services/api/api-client';
import { useGetStudyUserListQuery } from 'src/services/api/api-client/StudyQuery';
import { AppTable } from 'uikit/table/AppTable';
import { useSortBy } from 'uikit/table/updateSortByInUrl';
import { useQueryParams } from 'use-query-params';
import {
  defaultQueryParams,
  studyUsersFilterToBackendRequest,
  usersQueryParamsConfig,
} from './studyUserQueryParamsHelper';
import { ReactComponent as CopyIcon } from 'src/assets/img/common/copy_24.svg';
import { ReactComponent as NewCommentIcon } from 'assets/img/notification/report_filled_20.svg';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { isEmpty, isNil } from 'lodash';
import { AppButtonWithTooltip } from 'uikit/buttons/buttonWithTooltip/buttonWithTooltip';
import { showNotificationToast } from 'src/components/toast/toast-helper';
import { formatRelative } from 'date-fns';
import { getRelativeDateLocale } from 'src/application/localisation/localization';
import { Chips } from 'uikit/chips/chips.component';
import { Filters } from './studyUsersFilter';
import { useHasStudyClaims } from 'src/helpers/auth/auth-helper';
import { useNavigate } from 'react-router';
import { navigateToRoute } from 'src/navigation/routes';

export const StudyUsers = () => {
  const { id: studyId } = useStudy() ?? {};
  const { t } = useScopedTranslation('StudyUsersPage');

  const { hasStudyClaim } = useHasStudyClaims();
  const navigate = useNavigate();
  useLayoutEffect(() => {
    if (!hasStudyClaim(StudyRoleClaimValue.StudyUserListView)) navigate(navigateToRoute.dashboard(studyId!));
  }, [hasStudyClaim, navigate, studyId]);

  const [queryParams, setQueryParams] = useQueryParams(usersQueryParamsConfig);

  const { sortBy, desc } = defaultQueryParams;
  const sort = useSortBy(queryParams, setQueryParams, {
    default: {
      sortBy: sortBy!,
      desc: desc!,
    },
  });

  const userList = useGetStudyUserListQuery(
    // TODO: Adjust sorting for enum values
    // @ts-ignore
    {
      studyId: studyId!,
      ...pagingSortingToBackendRequest(queryParams),
      ...studyUsersFilterToBackendRequest(queryParams),
    },
    {
      enabled: !!studyId,
      keepPreviousData: true,
    },
  );

  const rowDataTable = useReactTable<IStudyStaffDto>({
    data: userList?.data?.data || [],
    manualSorting: true,
    onSortingChange: sort.onSortingChange,
    getRowId: (user) => user.id,
    state: {
      sorting: sort.sortingState,
    },
    columns: useUserColumns(),
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <TablePageLayout className={styles.tablePageLayout}>
      <TablePageLayout.Header>
        <TablePageLayout.Header.Row>
          <TablePageLayout.Header.Title text={t('Title')} secondaryText={userList.data?.totalCount.toString() ?? '-'} />
        </TablePageLayout.Header.Row>
        <Filters queryParams={queryParams} setQueryParams={setQueryParams} />
      </TablePageLayout.Header>
      <TablePageLayout.TableContainer loading={userList.isLoading || userList.isPreviousData}>
        <AppTable
          table={rowDataTable}
          placeholder={{ hide: userList.isLoading || userList.isPreviousData, text: t('NoData') }}
        />
        <TablePageLayout.TableContainer.Pagination
          page={queryParams.page}
          perPage={queryParams.perPage}
          totalCount={userList?.data?.totalCount || 0}
          changePagination={setQueryParams}
        />
      </TablePageLayout.TableContainer>
    </TablePageLayout>
  );
};

const useUserColumns = (): ColumnDef<IStudyStaffDto>[] => {
  const { t: tCommon } = useTranslation();
  const { t } = useScopedTranslation('StudyUsersPage.UsersTable');

  return useMemo(
    () =>
      [
        {
          id: 'fullName',
          accessorFn: (staff) => staff.fullName,
          header: t('Headers.FullName'),
          enableSorting: true,
          size: 240,
          meta: {},
        },
        {
          id: 'roles',
          header: t('Headers.Roles'),
          enableSorting: false,
          size: 200,
          cell: ({ row }) => <RolesColumn roles={row.original.roles} />,
          meta: { isColored: true },
        },
        {
          id: 'lastVisitDate',
          accessorFn: (staff) => staff.lastVisitDate,
          enableSorting: true,
          header: t('Headers.LastVisitDate'),
          cell: ({ row }) => (
            <div>
              {row.original.lastVisitDate
                ? formatRelative(row.original.lastVisitDate, Date.now(), { locale: getRelativeDateLocale() })
                : tCommon('Common_Never')}
            </div>
          ),
          size: 300,
          meta: {},
        },
        {
          id: 'email',
          header: t('Headers.Email'),
          cell: ({ row }) => <EmailCell email={row.original.email ?? ''} />,
          size: 300,
          meta: { isColored: true },
        },
        {
          id: 'tags',
          header: t('Headers.Tags'),
          cell: ({ row }) => <TagsColumn tags={row.original.tags} noTagsPlaceholder={t('NoTags')} />,
          meta: { dontUseHeaderWidth: true, cellClassName: styles.tagsCell },
        },
      ] satisfies ColumnDef<IStudyStaffDto>[],
    [t, tCommon],
  );
};

const RolesColumn: FC<{ roles: string[] | undefined }> = ({ roles }) => {
  const { t } = useScopedTranslation('StudyUsersPage.UsersTable');
  if (!roles?.length) return <div className={styles.noTags}>{t('NoRoles')}</div>;
  return <span>{roles.join(', ')}</span>;
};

const TagsColumn: FC<{ tags: ITagFiltersDto | null; noTagsPlaceholder?: string }> = ({ tags, noTagsPlaceholder }) => {
  if (isEmpty(tags)) return <div className={styles.noTags}>{noTagsPlaceholder}</div>;

  return (
    <div className={styles.tagColumn}>
      {Object.entries(tags)
        .sort()
        .map(([key, values]) => (
          <div key={key} className={clsx(styles.tag)}>
            <div className={styles.tagKey}>{`${key}:`}</div>
            {values.map((value: string) => (
              <Chips key={value} text={value} colorSchema={'blue'} />
            ))}
          </div>
        ))}
    </div>
  );
};

const EmailCell: FC<{ email?: string }> = ({ email }) => {
  const { t } = useScopedTranslation('StudyUsersPage.UsersTable');

  const copyHandler = useCallback(async () => {
    if (isNil(email)) return;
    await navigator.clipboard.writeText(email);
    showNotificationToast(
      t('EmailCopiedNotification.Message'),
      t('EmailCopiedNotification.Header'),
      NewCommentIcon,
      1000,
    );
  }, [email, t]);

  if (isNil(email)) return <></>;

  return (
    <div className={styles.emailCell}>
      <div>{email}</div>
      <AppButtonWithTooltip
        colorSchema={'primary'}
        variant={'icon-link'}
        Icon={CopyIcon}
        tooltip={t('CopyEmailTooltip')}
        onClick={copyHandler}
      />
    </div>
  );
};
